import { IconButton } from '@ads/components-react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';

import { downloadFile } from '../../../utils/downloadFile';
import { ProgressBar } from '../../ProgressBar/ProgressBar';

const styleConfig = tv({
  slots: {
    root: [
      'flex items-center gap-nano rounded-sm border-hairline border-ctx-interactive-secondary px-mini py-micro',
    ],
    icon: ['size-xxxs shrink-0 self-start pt-[0.125rem] text-ctx-content-title'],
    fileInfoWrapper: ['flex w-full flex-col gap-quark'],
    fileName: ['text-ctx-content-title ts-paragraph-xxs'],
    progressWrapper: ['text-ctx-content-base ts-paragraph-xxxs flex flex-col gap-quark'],
  },
  variants: {
    status: {
      uploading: '',
      failed: {
        root: '!border-base-danger-600',
        icon: '!text-base-danger-600',
        progressWrapper: '!text-base-danger-600',
      },
      succeeded: '',
      download: {
        icon: 'self-center',
      },
    },
  },
});

enum EStatus {
  Download = 'download',
  Uploading = 'uploading',
  Failed = 'failed',
  Succeeded = 'succeeded',
}

export interface FileCardProps {
  fileName: string;
  progress?: number;
  hasFailed?: boolean;
  removeFile?: () => void;
  downloadFile?: File;
}

export const FileCard: React.FC<FileCardProps> = ({
  fileName,
  progress,
  hasFailed,
  removeFile,
  downloadFile: file,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'fileUpload',
  });

  const status = (() => {
    if (file) {
      return EStatus.Download;
    }
    if (hasFailed) {
      return EStatus.Failed;
    }
    if (progress !== undefined && progress < 100) {
      return EStatus.Uploading;
    }
    return EStatus.Succeeded;
  })();

  const styles = styleConfig({ status });

  return (
    <div className={styles.root()}>
      {/* Icon */}
      {status === EStatus.Download ? (
        <DocumentIcon className={styles.icon()} />
      ) : status === EStatus.Uploading ? (
        <CloudArrowUpIcon className={styles.icon()} />
      ) : status === EStatus.Succeeded ? (
        <CheckCircleIcon className={styles.icon()} />
      ) : (
        <XCircleIcon className={styles.icon()} />
      )}

      {/* File Info */}
      <div className={styles.fileInfoWrapper()}>
        {/* File Name */}
        <p className={styles.fileName()}>{fileName}</p>

        {/* Progress Indicator */}
        {status !== EStatus.Download && (
          <div className={styles.progressWrapper()}>
            <p>
              {status === EStatus.Uploading
                ? t('uploading')
                : status === EStatus.Failed
                  ? t('failed')
                  : t('uploaded')}
            </p>

            {status === EStatus.Uploading && progress && (
              <ProgressBar shouldShowPercentage progressPercentage={progress} />
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="inline-flex items-center gap-nano">
        {/* Remove File Button */}
        {status !== EStatus.Uploading && removeFile && (
          <IconButton
            ariaLabel={t('fileDeleteButton')}
            hierarchy="tertiary"
            icon={TrashIcon}
            size="sm"
            onClick={removeFile}
          />
        )}

        {/* Download File Button */}
        {status === EStatus.Download && file && (
          <IconButton
            ariaLabel={t('fileDownloadButton')}
            hierarchy="tertiary"
            icon={ArrowDownTrayIcon}
            size="sm"
            onClick={() => downloadFile({ fileName: file.name, data: file })}
          />
        )}
      </div>
    </div>
  );
};
